import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function WorkerReputation() {
    const [reputation, setReputation] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'WORKER') {
            navigate('/');
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Reputation
            const repRes = await axios.get(`${API_URL}/api/worker/reputation`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReputation(repRes.data);

            // Fetch Submission History
            const subsRes = await axios.get(`${API_URL}/api/worker/submissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmissions(subsRes.data || []);
            
        } catch (err) {
            console.error('Failed to load data', err);
            toast.error('Failed to load reputation data');
        }
        setLoading(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    if (loading) {
        return (
            <div className="dashboard-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
            </div>
        );
    }

    // Helpers for UI
    const tier = reputation?.tier || 'NEW';
    const isSuspended = reputation?.suspendedUntil && new Date(reputation.suspendedUntil) > new Date();
    
    // Calculate progress to next tier (Handles both NEW and UNVERIFIED)
    const getProgressToNextTier = () => {
        const completed = reputation?.completedTasks || 0;
        
        if (tier === 'NEW' || tier === 'UNVERIFIED') {
            return { next: 'TRUSTED', required: 5, current: completed, percent: (completed / 5) * 100 };
        }
        if (tier === 'TRUSTED') {
            return { next: 'GOLD', required: 25, current: completed, percent: (completed / 25) * 100 };
        }
        if (tier === 'GOLD') {
            return { next: 'PLATINUM', required: 100, current: completed, percent: (completed / 100) * 100 };
        }
        return { next: 'MAX', required: 100, current: 100, percent: 100 };
    };

    const progress = getProgressToNextTier();

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING': case 'PENDING_BUSINESS': return 'badge badge-warning';
            case 'APPROVED': return 'badge badge-success';
            case 'REJECTED': return 'badge badge-error';
            default: return 'badge';
        }
    };

    return (
        <div className="dashboard-layout">
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    <Link to="/worker-dashboard" className="nav-item">
                        <span>📊</span> Overview
                    </Link>
                    <Link to="/worker/reputation" className={`nav-item ${location.pathname === '/worker/reputation' ? 'active' : ''}`}>
                        <span>🏆</span> Reputation
                    </Link>
                    <Link to="/wallet" className="nav-item">
                        <span>💰</span> Wallet
                    </Link>
                    <Link to="/profile" className="nav-item">
                        <span>👤</span> Profile
                    </Link>
                </nav>
                
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Reputation & History</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    
                    {/* SUSPENSION BANNER */}
                    {isSuspended && (
                        <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--error)', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <span style={{ fontSize: '24px' }}>⚠️</span>
                            <div>
                                <h4 style={{ color: 'var(--error)', marginBottom: '4px', fontSize: '16px' }}>Account Suspended</h4>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
                                    You cannot accept new tasks until {new Date(reputation.suspendedUntil).toLocaleString()}.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* REPUTATION OVERVIEW */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                        
                        {/* Tier Card */}
                        <div className="card" style={{ padding: '32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Current Tier</p>
                            <h2 style={{ 
                                fontSize: '48px', 
                                fontWeight: '700', 
                                letterSpacing: '-0.04em',
                                color: tier === 'PLATINUM' ? '#e5e5e5' : tier === 'GOLD' ? '#fbbf24' : tier === 'TRUSTED' ? '#10b981' : '#ffffff',
                                marginBottom: '8px'
                            }}>
                                {tier}
                            </h2>
                            
                            {tier !== 'PLATINUM' && (
                                <div style={{ width: '100%', marginTop: '24px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                        <span>{progress.current} / {progress.required} tasks</span>
                                        <span>Next: {progress.next}</span>
                                    </div>
                                    <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${Math.min(progress.percent, 100)}%`, background: 'var(--accent)', borderRadius: '3px' }}></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Stats Card */}
                        <div className="card" style={{ padding: '32px' }}>
                            <h3 style={{ fontSize: '16px', marginBottom: '24px', color: 'var(--text)' }}>Performance</h3>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Approval Rate</span>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: (reputation?.approvalRate || 0) >= 90 ? 'var(--success)' : 'var(--warning)' }}>
                                    {reputation?.approvalRate ? `${reputation.approvalRate.toFixed(1)}%` : 'N/A'}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Honor Score</span>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--info)' }}>
                                    {reputation?.honorScore || 0}
                                </span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Tasks Approved</span>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--success)' }}>
                                    {reputation?.completedTasks || 0}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Tasks Rejected</span>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--error)' }}>
                                    {submissions.filter(s => s.status === 'REJECTED').length || 0}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* SUBMISSION HISTORY */}
                    <div style={{ marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '18px', marginBottom: '4px', color: 'var(--text)' }}>Task History</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>A complete log of your submitted work.</p>
                    </div>

                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Task</th>
                                    <th>Reward</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {submissions.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                            No task history available.
                                        </td>
                                    </tr>
                                ) : (
                                    submissions.map(sub => (
                                        <tr key={sub.id}>
                                            <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                                {new Date(sub.submittedAt).toLocaleDateString()}
                                            </td>
                                            <td style={{ fontWeight: '500', color: 'var(--text)' }}>
                                                {sub.task?.title || 'Unknown Task'}
                                            </td>
                                            <td style={{ color: 'var(--success)', fontWeight: '500' }}>
                                                ${sub.task?.reward || '0.00'}
                                            </td>
                                            <td>
                                                <span className={getStatusBadge(sub.status)}>
                                                    {sub.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                </main>
            </div>
        </div>
    );
}

export default WorkerReputation;